import { createContext, useContext, useState, useMemo, type ReactNode } from "react";

export type ImpactMode = "ctg" | "ops";

export interface ScaleRatios {
    co2e: number;
    dalys: number;
    pdfs: number;
    usd: number;
}

interface ImpactModeContextValue {
    mode: ImpactMode;
    setMode: (mode: ImpactMode) => void;
    scaleRatios: ScaleRatios;
}

const ImpactModeContext = createContext<ImpactModeContextValue | null>(null);

interface Totals {
    DALYs: number;
    PDFs: number;
    USD: number;
}

interface ImpactModeProviderProps {
    netTotals: Totals | null;
    opsTotals: Totals | null;
    children: ReactNode;
}

function safeRatio(net: number, ops: number): number {
    if (ops === 0) return 1;
    return net / ops;
}

export function ImpactModeProvider({ netTotals, opsTotals, children }: ImpactModeProviderProps) {
    const [mode, setMode] = useState<ImpactMode>("ctg");

    const scaleRatios = useMemo<ScaleRatios>(() => {
        if (mode === "ops" || !netTotals || !opsTotals) {
            return { co2e: 1, dalys: 1, pdfs: 1, usd: 1 };
        }
        // Time-series data is ops-based; scale to net
        return {
            co2e: safeRatio(netTotals.DALYs, opsTotals.DALYs), // co2e scales similarly
            dalys: safeRatio(netTotals.DALYs, opsTotals.DALYs),
            pdfs: safeRatio(netTotals.PDFs, opsTotals.PDFs),
            usd: safeRatio(netTotals.USD, opsTotals.USD),
        };
    }, [mode, netTotals, opsTotals]);

    return (
        <ImpactModeContext.Provider value={{ mode, setMode, scaleRatios }}>
            {children}
        </ImpactModeContext.Provider>
    );
}

export function useImpactMode(): ImpactModeContextValue {
    const ctx = useContext(ImpactModeContext);
    if (!ctx) {
        // Fallback when used outside provider (e.g. admin pages before wiring)
        return {
            mode: "ctg",
            setMode: () => {},
            scaleRatios: { co2e: 1, dalys: 1, pdfs: 1, usd: 1 },
        };
    }
    return ctx;
}
